/**
 * Iter 55 — shared tiny SVG sparkline. Originally lived inline in
 * CommandPaletteSearch; extracted here so the Undo analytics card can
 * reuse the same visual language for its 7-day per-kind trends.
 *
 * ``values``: array of numeric closes. Values with < 2 points render null
 * (caller decides whether to reserve space).
 * ``up``: colour hint — green when true, red when false. If omitted,
 * defaults to green (neutral positive).
 * ``width`` / ``height``: pixels. Range dots always sit at r=1.5.
 * ``ariaLabel``: optional accessible name; auto-generated when absent.
 *
 * Iter 58 — optional ``annotations`` prop (same length as values). Each
 * entry can be:
 *   • a string → used as both the SVG <title> (hover tooltip) AND the
 *     compact label rendered above the dot;
 *   • { title, label } → separate values for the tooltip vs the label.
 * When ``annotations`` is present, every data point gets a small circle
 * dot so the labels have something to attach to.
 */
export default function MiniSparklineSVG({
  values,
  up = true,
  width = 60,
  height = 16,
  ariaLabel,
  annotations,
  showLegend = false,
  focusableTicks = false,
  onTickFocus,
  onTickBlur,
  ...rest
}) {
  if (!values || values.length < 2) return null;
  // Trim to a rolling window so the visual stays crisp regardless of the
  // caller's data density.
  const points = values.slice(-24);
  const trimmedAnnotations = Array.isArray(annotations) ? annotations.slice(-24) : null;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  // Iter 58 — reserve top padding for annotation labels when present.
  const labelPad = trimmedAnnotations ? 8 : 0;
  const plotH = height - labelPad;
  const pt = (v, i) => {
    const x = (i / (points.length - 1)) * width;
    const y = labelPad + (plotH - ((v - min) / range) * plotH);
    return { x, y };
  };
  const path = points
    .map((v, i) => {
      const { x, y } = pt(v, i);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const color = up ? "#00C805" : "#FF3B30";
  const maxIdx = points.indexOf(max);
  const minIdx = points.indexOf(min);
  // Iter 80 — Delta Colour Gradient. When annotations expose numeric
  // ``delta`` values, render the polyline as per-segment strokes
  // coloured grey → amber proportional to |Δ| / max|Δ| so the
  // biggest jump reads visually even without hovering. Falls back
  // to the single-stroke path (above) when no deltas are present.
  const segmentDeltas = trimmedAnnotations
    ? trimmedAnnotations.map((ann) =>
        ann && typeof ann === "object" && ann.delta != null ? Math.abs(ann.delta) : null,
      )
    : null;
  const maxAbsDelta = segmentDeltas
    ? Math.max(0, ...segmentDeltas.filter((d) => d != null))
    : 0;
  const useGradient = maxAbsDelta > 0;
  const segmentColor = (i) => {
    const d = segmentDeltas ? segmentDeltas[i] : null;
    if (d == null || maxAbsDelta === 0) return "#3f3f46";
    const t = Math.min(1, d / maxAbsDelta);
    // #52525b (zinc-600 rgb 82,82,91) → #FBBF24 (amber-400 rgb 251,191,36)
    const r = Math.round(82 + (251 - 82) * t);
    const g = Math.round(82 + (191 - 82) * t);
    const b = Math.round(91 + (36 - 91) * t);
    return `rgb(${r},${g},${b})`;
  };
  const svg = (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="shrink-0"
      aria-label={ariaLabel || "sparkline"}
      {...rest}
    >
      {useGradient ? (
        // Iter 80 — per-segment strokes coloured grey → amber by |Δ|.
        // Each segment from point i to i+1 uses the delta of point i+1
        // (the delta is defined vs the *previous* row).
        points.slice(1).map((v, i) => {
          const a = pt(points[i], i);
          const b = pt(v, i + 1);
          return (
            <line
              key={`seg-${i}`}
              data-testid={`sparkline-segment-${i}`}
              data-delta-abs={segmentDeltas[i + 1] ?? ""}
              x1={a.x.toFixed(1)}
              y1={a.y.toFixed(1)}
              x2={b.x.toFixed(1)}
              y2={b.y.toFixed(1)}
              stroke={segmentColor(i + 1)}
              strokeWidth="1.25"
              strokeLinecap="round"
            />
          );
        })
      ) : (
        <path
          d={path}
          fill="none"
          stroke={color}
          strokeWidth="1.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {(() => {
        const { x: mx, y: my } = pt(max, maxIdx);
        return <circle cx={mx.toFixed(1)} cy={my.toFixed(1)} r={1.5} fill="#00C805" />;
      })()}
      {(() => {
        const { x: nx, y: ny } = pt(min, minIdx);
        return <circle cx={nx.toFixed(1)} cy={ny.toFixed(1)} r={1.5} fill="#FF3B30" />;
      })()}
      {/* Iter 58 — per-point annotation dots + labels. Each dot carries
          the full attribution as an SVG <title> (native tooltip); the
          label is rendered above so admins can eyeball who ran which
          experiment without hovering.
          Iter 59 — an annotation with ``highlight: true`` is rendered
          in gold so the winning datapoint pops (e.g. best-CTR
          threshold in the A/B history sparkline). */}
      {trimmedAnnotations && points.map((v, i) => {
        const ann = trimmedAnnotations[i];
        if (!ann) return null;
        const { x, y } = pt(v, i);
        const title = typeof ann === "string" ? ann : (ann.title || "");
        const label = typeof ann === "string" ? ann : (ann.label || "");
        const highlight = typeof ann === "object" && ann.highlight === true;
        // Iter 79 — Chart Micro-Legend. A `lowlight: true` annotation
        // renders a paired red counter-marker so admins see both the
        // highest AND lowest CTR endpoints on the sparkline without
        // hovering.
        const lowlight = typeof ann === "object" && ann.lowlight === true;
        const dotStroke = highlight ? "#FBBF24" : lowlight ? "#FF3B30" : color;
        const dotFill = highlight ? "#FBBF24" : lowlight ? "#FF3B30" : "#0a0a0a";
        const dotR = highlight || lowlight ? 3 : 2;
        const dataDelta = typeof ann === "object" && ann.delta != null ? String(ann.delta) : undefined;
        const isBiggestDelta = typeof ann === "object" && ann.biggestDelta === true;
        return (
          <g
            key={`ann-${i}`}
            data-annotation-index={i}
            data-highlight={highlight ? "true" : "false"}
            data-lowlight={lowlight ? "true" : "false"}
            data-delta={dataDelta}
            data-biggest-delta={isBiggestDelta ? "true" : "false"}
            // Iter 84 — Popover Focus Trap. When `focusableTicks` is
            // on, each annotation <g> becomes a focusable stop so
            // keyboard users can Tab through the sparkline and hear
            // its title via the accessible name (aria-label). Uses
            // tabIndex=0 + role="button" — SVG-native focus works
            // in all modern browsers.
            {...(focusableTicks
              ? {
                  tabIndex: 0,
                  role: "button",
                  "aria-label": title || undefined,
                  "data-testid": `sparkline-tick-${i}`,
                  className: "outline-none focus-visible:[&_circle]:stroke-amber-400 focus-visible:[&_circle]:stroke-[2px]",
                  onFocus: () => { if (onTickFocus) onTickFocus(i, (x / width) * 100); },
                  onBlur: () => { if (onTickBlur) onTickBlur(i); },
                  // Iter 87 — Tick Chip Arrow Nav. Left/Right cycle
                  // focus between adjacent ticks (wrap at ends);
                  // Home/End jump to first/last. Keeps the popover
                  // Tab-order sane — users no longer have to Tab
                  // through 7 dots to reach the next control.
                  onKeyDown: (e) => {
                    const arrows = ["ArrowLeft", "ArrowRight", "Home", "End"];
                    if (!arrows.includes(e.key)) return;
                    e.preventDefault();
                    e.stopPropagation();
                    const svgEl = e.currentTarget.ownerSVGElement || e.currentTarget.closest("svg");
                    if (!svgEl) return;
                    const ticks = Array.from(
                      svgEl.querySelectorAll("[data-testid^='sparkline-tick-']"),
                    );
                    const count = ticks.length;
                    if (count === 0) return;
                    let nextIdx = i;
                    if (e.key === "ArrowLeft") nextIdx = (i - 1 + count) % count;
                    else if (e.key === "ArrowRight") nextIdx = (i + 1) % count;
                    else if (e.key === "Home") nextIdx = 0;
                    else if (e.key === "End") nextIdx = count - 1;
                    const target = ticks[nextIdx];
                    if (target && typeof target.focus === "function") target.focus();
                  },
                }
              : {})}
          >
            <circle
              cx={x.toFixed(1)}
              cy={y.toFixed(1)}
              r={dotR}
              fill={dotFill}
              stroke={dotStroke}
              strokeWidth={highlight || lowlight ? "1.5" : "1"}
            >
              {title && <title>{title}</title>}
            </circle>
            {label && (
              <text
                x={x.toFixed(1)}
                y={Math.max(6, y - 3).toFixed(1)}
                fontSize="6"
                fill={highlight ? "#FBBF24" : lowlight ? "#FF3B30" : "#71717a"}
                textAnchor="middle"
                fontFamily="ui-monospace, monospace"
              >
                {label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
  // Iter 81 — Colour-Legend Tooltip. When ``showLegend`` is enabled
  // and the sparkline is in gradient mode, render a small
  // grey→amber swatch strip alongside the SVG with a native
  // ``title`` tooltip so first-time viewers understand what the
  // colour ramp means. Falls back to the bare SVG otherwise so
  // existing callers keep their exact DOM shape.
  if (!showLegend || !useGradient) return svg;
  return (
    <span
      data-testid="sparkline-with-legend"
      className="inline-flex items-center gap-1 align-middle"
    >
      {svg}
      <span
        data-testid="sparkline-colour-legend"
        className="inline-flex items-center gap-0.5 h-2"
        title="Δ magnitude: grey = small change, amber = biggest jump"
        aria-label="Colour legend: grey to amber represents delta magnitude"
      >
        {[0.15, 0.4, 0.7, 1].map((t, i) => {
          const r = Math.round(82 + (251 - 82) * t);
          const g = Math.round(82 + (191 - 82) * t);
          const b = Math.round(91 + (36 - 91) * t);
          return (
            <span
              key={`swatch-${i}`}
              data-testid={`sparkline-legend-swatch-${i}`}
              className="inline-block w-1.5 h-2 rounded-[1px]"
              style={{ backgroundColor: `rgb(${r},${g},${b})` }}
              aria-hidden="true"
            />
          );
        })}
      </span>
    </span>
  );
}
